import React, { useEffect, useRef, useState } from 'react';
const WavEncoder = require("wav-encoder");

// Utility function to convert audio Blob to ArrayBuffer
const readBlobAsArrayBuffer = (blob: Blob): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = reject;
        reader.readAsArrayBuffer(blob);
    });
};

// Utility function to convert audio Blob to WAV format
const convertToWav = async (audioBuffer: AudioBuffer, sampleRate: number): Promise<Blob> => {
    const audioContext = new (window as any).AudioContext();

    try {
        const resampledAudioBuffer = audioContext.createBuffer(
            audioBuffer.numberOfChannels,
            Math.floor(audioBuffer.length * sampleRate / audioBuffer.sampleRate),
            sampleRate
        );

        for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
            const inputData = audioBuffer.getChannelData(channel);
            const outputData = resampledAudioBuffer.getChannelData(channel);
            const resampleRatio = audioBuffer.sampleRate / sampleRate;

            for (let i = 0; i < outputData.length; i++) {
                const sampleIndex = Math.floor(i * resampleRatio);
                outputData[i] = inputData[sampleIndex];
            }
        }

        const wavData = await WavEncoder.encode({
            sampleRate: sampleRate,
            channelData: Array.from({ length: resampledAudioBuffer.numberOfChannels }, (_, i) => resampledAudioBuffer.getChannelData(i)),
            bitDepth: 32
        });

        return new Blob([wavData], { type: 'audio/wav' });
    } catch (error) {
        console.error('Error processing audio:', error);
        throw error;
    }
};

const AudioStream: React.FC = () => {
    const [status, setStatus] = useState<string>('Starting...');
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const socketRef = useRef<WebSocket | null>(null);

    useEffect(() => {
        const socket = new WebSocket('ws://localhost:5000/audio-stream');
        socketRef.current = socket;

        socket.onopen = () => {
            setStatus('WebSocket connected.');
        };

        socket.onclose = () => {
            setStatus('WebSocket disconnected.');
        };

        socket.onerror = (error) => {
            console.error('WebSocket error:', error);
            setStatus('WebSocket error.');
        };

        navigator.mediaDevices.getUserMedia({ audio: true })
            .then(stream => {
                mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });

                mediaRecorderRef.current.ondataavailable = async (event) => {
                    if (event.data.size > 0 && socketRef.current?.readyState === WebSocket.OPEN) {
                        try {
                            const arrayBuffer = await readBlobAsArrayBuffer(event.data);
                            const audioContext = new (window as any).AudioContext();

                            // Log ArrayBuffer for debugging
                            console.log('ArrayBuffer length:', arrayBuffer.byteLength);

                            const audioBuffer = await new Promise<AudioBuffer>((resolve, reject) => {
                                audioContext.decodeAudioData(arrayBuffer, resolve, reject);
                            });

                            // Log audio buffer details
                            console.log('AudioBuffer:', audioBuffer);
                            console.log('Sample rate:', audioBuffer.sampleRate);
                            console.log('Channels:', audioBuffer.numberOfChannels);
                            console.log('Length:', audioBuffer.length);

                            const wavBlob = await convertToWav(audioBuffer, 16000);
                            const wavArrayBuffer = await readBlobAsArrayBuffer(wavBlob);

                            socketRef.current?.send(wavArrayBuffer);
                        } catch (error) {
                            console.error('Error processing audio:', error);
                        }
                    }
                };

                mediaRecorderRef.current.start(1000); // Send data every 10 seconds
                setStatus('Recording...');
            })
            .catch(error => {
                console.error('Error accessing microphone:', error);
                setStatus('Error accessing microphone.');
            });

        return () => {
            if (mediaRecorderRef.current) {
                mediaRecorderRef.current.stop();
            }
            if (socketRef.current) {
                socketRef.current.close();
            }
        };
    }, []);

    return (
        <div>
            <h1>Audio Streaming</h1>
            <p>{status}</p>
        </div>
    );
};

export default AudioStream;
